import React, { useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography
} from '@mui/material';
import {
  CheckCircle as CompleteIcon,
  EmojiEvents as CertificateIcon,
  PlayArrow as StartIcon,
  School as ModuleIcon,
  Visibility as ViewIcon
} from '@mui/icons-material';
import { THEME_COLORS } from '../../../styles/theme';
import {
  enrolLearningPath,
  getAudienceLabel,
  toggleLearningModuleCompletion
} from '../services/professionalDevelopmentService';

const ProfessionalLearning = ({ userId, userProfile, record, onDataChanged }) => {
  const [error, setError] = useState('');
  const learningPaths = useMemo(() => record?.learningCatalog || [], [record?.learningCatalog]);
  const enrolledPaths = useMemo(() => record?.enrolledPaths || [], [record?.enrolledPaths]);
  const audienceLabel = getAudienceLabel(userProfile || {});

  const enrolledIds = useMemo(
    () => new Set(enrolledPaths.map((path) => path.sourcePathId)),
    [enrolledPaths]
  );

  const handleEnroll = async (pathId) => {
    try {
      setError('');
      await enrolLearningPath(userId, pathId, userProfile || {});
      await onDataChanged?.();
    } catch (enrollError) {
      console.error('Error enrolling learning pathway:', enrollError);
      setError('Unable to start that learning pathway right now.');
    }
  };

  const handleModuleToggle = async (pathId, moduleId) => {
    try {
      setError('');
      await toggleLearningModuleCompletion(userId, pathId, moduleId, userProfile || {});
      await onDataChanged?.();
    } catch (toggleError) {
      console.error('Error updating learning module:', toggleError);
      setError('Unable to update that learning module right now.');
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Active learning for {audienceLabel.toLowerCase()} growth
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start structured pathways, track module completion, and build evidence of professional learning.
          </Typography>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            My learning progress
          </Typography>

          {enrolledPaths.length === 0 && (
            <Typography color="text.secondary">You have not started a pathway yet.</Typography>
          )}

          {enrolledPaths.map((path) => (
            <Card key={path.id} variant="outlined" sx={{ mb: 2 }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">{path.title}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Started {path.startedAt} • {path.completedModules}/{path.totalModules} modules completed
                    </Typography>
                  </Box>
                  <Chip
                    label={path.progress === 100 ? 'Completed' : `${path.progress}% complete`}
                    color={path.progress === 100 ? 'success' : 'primary'}
                  />
                </Box>

                <LinearProgress
                  variant="determinate"
                  value={path.progress || 0}
                  sx={{
                    height: 8,
                    borderRadius: 999,
                    mb: 2,
                    bgcolor: 'rgba(39, 35, 97, 0.08)',
                    '& .MuiLinearProgress-bar': { bgcolor: THEME_COLORS.primary }
                  }}
                />

                <List dense>
                  {path.modules.map((module, index) => {
                    const previousModule = index === 0 ? null : path.modules[index - 1];
                    const locked = previousModule ? !previousModule.completed : false;

                    return (
                      <ListItem key={module.id}>
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: module.completed ? 'success.main' : locked ? 'grey.300' : THEME_COLORS.primary
                            }}
                          >
                            {module.completed ? <CompleteIcon fontSize="small" /> : index + 1}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={module.title}
                          secondary={`${module.duration} minutes`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            size="small"
                            disabled={locked}
                            onClick={() => handleModuleToggle(path.id, module.id)}
                          >
                            <ViewIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    );
                  })}
                </List>

                {path.certificateEarned && (
                  <Box mt={2} p={2} sx={{ bgcolor: 'rgba(255, 215, 0, 0.12)', borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CertificateIcon sx={{ color: '#b8860b' }} />
                      <Typography variant="body2" fontWeight="bold">
                        Certificate earned for this pathway
                      </Typography>
                    </Box>
                  </Box>
                )}
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Available learning pathways
          </Typography>

          <Grid container spacing={2}>
            {learningPaths.map((path) => (
              <Grid item xs={12} md={6} lg={4} key={path.id}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                      <Avatar sx={{ bgcolor: THEME_COLORS.primary }}>
                        <ModuleIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">{path.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {path.modules.length} modules • {path.estimatedHours}h
                        </Typography>
                      </Box>
                    </Box>

                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {path.description}
                    </Typography>

                    <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                      <Chip label={path.category} size="small" sx={{ bgcolor: 'rgba(39, 35, 97, 0.08)' }} />
                      <Chip label={path.level} size="small" color={path.level === 'Beginner' ? 'success' : path.level === 'Advanced' ? 'error' : 'warning'} />
                    </Box>

                    <Button
                      variant={enrolledIds.has(path.id) ? 'outlined' : 'contained'}
                      startIcon={<StartIcon />}
                      fullWidth
                      onClick={() => handleEnroll(path.id)}
                      disabled={enrolledIds.has(path.id)}
                      sx={enrolledIds.has(path.id)
                        ? { color: THEME_COLORS.primary, borderColor: THEME_COLORS.primary }
                        : { bgcolor: THEME_COLORS.primary }
                      }
                    >
                      {enrolledIds.has(path.id) ? 'Already started' : 'Start learning'}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProfessionalLearning;
